import type { PopupSlotCreateInput } from '../../types/cms';

/**
 * C-1 (반복: 4차 L-2 · 9차 L · ID36): 팝업 *등록(신규)* 폼의 예약 슬롯 추가 로직.
 *
 * 등록 페이지에는 영속 popupId 가 아직 없어 슬롯을 즉시 API 로 만들 수 없으므로(수정 페이지의
 * PopupSlotSection 은 popupId 기반 mutation 사용), 로컬 배열(form.slots)에 누적했다가 create 요청
 * 본문의 `slots[]` 로 함께 전송한다. '슬롯 추가'가 동작하지 않는 회귀를 막기 위해, 추가 가능 여부
 * 판정(canAddSlotDraft)과 누적 append(appendSlotDraft)를 순수 함수로 분리해 단위 테스트로 고정한다.
 *
 * 시간은 5분 단위 "HH:mm"(TimeSelect5), 정원은 별도 문자열 입력(slotMaxInput)을 받아 정수화한다.
 *
 * QA-R6 N: 정원 0 기본값(footgun) 제거 + **미입력=무제한**. BE 가 maxParticipants≤0 을 무제한으로
 * 해석하므로(Lead 확정), 빈 입력은 0(=무제한)으로 보낸다. 종전 ≥1 강제(빈값=추가 불가)를 완화한다.
 */

export interface SlotDraftInput {
  slotDate: string;
  timeStart: string;
  timeEnd: string;
}

/** 무제한(정원 제한 없음) sentinel — BE 가 maxParticipants≤0 을 무제한으로 해석(QA-R6 N). */
export const SLOT_UNLIMITED = 0;

/**
 * 정원 문자열을 정수 정원으로 파싱. **빈 입력 = 무제한(0)**. 음수도 무제한(0)으로 정규화한다.
 * 양의 정수면 그 값, 비정수(소수·문자)면 null(무효 입력 → 추가 차단). QA-R6 N: 종전 "빈값/0=null(무효)"
 * 에서 "빈값/0/음수=무제한(0)" 으로 변경.
 */
export const parseSlotMax = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return SLOT_UNLIMITED; // 미입력 = 무제한.
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return null; // 소수/문자 등 무효 입력.
  return n > 0 ? n : SLOT_UNLIMITED; // 0·음수 = 무제한.
};

/** 슬롯 추가 가능 여부 — 날짜/시작/종료가 모두 채워지고 정원 입력이 유효할 때(빈값=무제한 허용). */
export const canAddSlotDraft = (draft: SlotDraftInput, slotMaxInput: string): boolean =>
  draft.slotDate !== '' &&
  draft.timeStart !== '' &&
  draft.timeEnd !== '' &&
  parseSlotMax(slotMaxInput) != null;

/**
 * 유효한 슬롯 1행을 기존 목록에 누적한 새 배열을 반환(불변). 입력이 유효하지 않으면 원본을 그대로 반환해
 * '추가' 가 조용히 실패하지 않도록 호출부가 canAddSlotDraft 로 가드하게 한다.
 */
export const appendSlotDraft = (
  slots: ReadonlyArray<PopupSlotCreateInput>,
  draft: SlotDraftInput,
  slotMaxInput: string,
): PopupSlotCreateInput[] => {
  const max = parseSlotMax(slotMaxInput);
  if (!canAddSlotDraft(draft, slotMaxInput) || max == null) return [...slots];
  return [...slots, { ...draft, maxParticipants: max }];
};
